import React from "react";

import Pagination from "./components/Pagination";
import Toolbar from "./components/Toolbar";
import TableBody from "./components/TableBody";
import Export from "./util/Export";
import TableSchema from "./models/TableSchema";

interface TableProps<T> {
  tableSchema: TableSchema;
  /** Column keys that will be used to access TableSchema */
  columns: string[];
  /**
   * The total rows count in the table (from database, for example). It
   * is used to detect pagination count and fetch items on page change
   */
  totalItemsCount: number;
  /**
   * Items should have the same keys as TableSchema, because table
   * will try to access data through these keys. For example,
   *
   * TableSchema {
   *  a: { ... }
   *  b: { ... }
   * }
   *
   * T {
   *  a: ...
   *  b: ...
   * }
   */
  items: T[];
  // search
  isDisplaySearch?: boolean;
  searchPlaceholder?: string;
  // external methods
  fetchItems(offset: number, limit: number, searchQuery?: string): void;
  fetchItemsWithCallback(
    offset: number,
    limit: number,
    searchQuery: string | undefined,
    resultCallback: (result: T[]) => void
  ): void;
  /**
   * Items that were passed through {columns} in custom order
   * if a user changed columns order\visibility
   */
  renderRow(orderedColumns: string[], item: T): JSX.Element;

  onSortColumn(columnName: string | undefined, isSortUp: boolean | undefined, offset: number, limit: number): void;
}

interface TableState<T> {
  tableSchema: TableSchema;
  isExportEnabled: boolean;
  // columns
  allOrderedColumns: string[];
  visibleColumns: string[];
  // items
  totalItemsCount: number;
  limit: number;
  offset: number;
  items: T[];
  // search text
  searchQuery: string;
  // sorted
  sortedColumnName?: string;
  isSortUp: boolean;
}

export default class Table<T> extends React.Component<TableProps<T>, TableState<T>> {
  public constructor(props: TableProps<T>) {
    super(props);

    this.state = {
      tableSchema: props.tableSchema,
      isExportEnabled: true,
      // columns
      allOrderedColumns: props.columns,
      visibleColumns: props.columns,
      // items
      totalItemsCount: props.totalItemsCount,
      limit: 25,
      offset: 0,
      items: [],
      // search
      searchQuery: "",
      // sort
      sortedColumnName: undefined,
      isSortUp: true,
    };
  }

  public componentDidMount = (): void => {
    this.reloadItems();
  };

  public componentDidUpdate = (prevProps: TableProps<T>): void => {
    if (prevProps.totalItemsCount !== this.props.totalItemsCount || prevProps.items !== this.props.items) {
      this.setState(
        {
          items: this.props.items,
          totalItemsCount: this.props.totalItemsCount,
        },
        () => {
          if (prevProps.totalItemsCount !== this.props.totalItemsCount) {
            this.reloadItems();
          }
        }
      );
    }
  };

  public setColumnsVisibility = (visibleColumns: string[]): void => {
    this.setState({
      visibleColumns,
    });
  };

  public setColumnsOrder = (allOrderedColumns: string[]): void => {
    this.setState({
      allOrderedColumns,
    });
  };

  public onExportTable = (): void => {
    const { totalItemsCount, allOrderedColumns, visibleColumns, searchQuery } = this.state;

    const exportedColumns: string[] = [];
    allOrderedColumns.forEach((column) => {
      if (visibleColumns.includes(column)) {
        exportedColumns.push(column);
      }
    });

    this.setState({ isExportEnabled: false }, () => {
      this.props.fetchItemsWithCallback(0, totalItemsCount, searchQuery, (items) => {
        new Export<T>().exportInXlsx(exportedColumns, items, () => {
          this.setState({ isExportEnabled: true });
        });
      });
    });
  };

  public setPaginationParams = (offset: number, limit: number): void => {
    this.setState({ limit, offset }, () => this.reloadItems());
  };

  public render(): JSX.Element {
    const {
      tableSchema,
      isExportEnabled,
      // columns
      allOrderedColumns,
      visibleColumns,
      // items
      totalItemsCount,
      limit,
      offset,
      items,
      // search
      searchQuery,
      // sort
      sortedColumnName,
      isSortUp,
    } = this.state;
    const { renderRow } = this.props;

    const paginationComponent = (
      <Pagination
        total={totalItemsCount}
        limit={limit}
        offset={offset}
        setPaginationParams={this.setPaginationParams}
      />
    );

    return (
      <div className="row">
        <div className="col bg-white border-left border-bottom border-right table-dark">
          <Toolbar
            // columns
            tableSchema={tableSchema}
            allOrderedColumns={allOrderedColumns}
            visibleColumns={visibleColumns}
            setColumnsVisibility={this.setColumnsVisibility}
            setColumnsOrder={this.setColumnsOrder}
            // export
            onExportTable={this.onExportTable}
            isExportEnabled={isExportEnabled}
            // search
            isDisplaySearch={!!this.props.isDisplaySearch}
            searchQuery={searchQuery}
            onSearchChanged={this.onSearchChanged}
            searchPlaceholder={this.props.searchPlaceholder}
          />

          {paginationComponent}

          <TableBody
            // columns
            tableSchema={tableSchema}
            allOrderedColumns={allOrderedColumns}
            visibleColumns={visibleColumns}
            // items
            items={items}
            renderRow={renderRow}
            onSortColumn={(sortedColumnName, isSortUp) => {
              this.setState({ sortedColumnName: sortedColumnName, isSortUp: isSortUp });
              this.props.onSortColumn(sortedColumnName, isSortUp, offset, limit);
            }}
            sortedColumnName={sortedColumnName}
            isSortedUp={isSortUp}
          />

          {paginationComponent}
        </div>
      </div>
    );
  }

  private reloadItems = (): void => {
    const { offset, limit, searchQuery } = this.state;
    this.props.fetchItems(offset, limit, searchQuery);
  };

  private onSearchChanged = (searchQuery: string): void => {
    this.setState({ searchQuery }, () => {
      this.reloadItems();
    });
  };
}
